/// <reference path="./lancer-data.d.ts" />
import lancerData from "@massif/lancer-data";
import { objectOfArraysToRawPack, type RawPack } from "./pack";
import type { ItemType } from "./schemas";

/**
 * Maps our compendium item-type keys to the corresponding keys on the
 * `@massif/lancer-data` default export. `@massif/lancer-data` exposes the core
 * book as one object of arrays (`frames`, `weapons`, …); it ships NPC arrays as
 * empty (NPC content lives in the separate `@massif/lancer-npc-data` package),
 * and has no `licenses` key (core licenses are implied by frame fields).
 */
const DATA_KEYS: Record<ItemType, keyof typeof lancerData | null> = {
  frame: "frames",
  weapon: "weapons",
  system: "systems",
  mod: "mods",
  talent: "talents",
  skill: "skills",
  background: "backgrounds",
  tag: "tags",
  license: null, // not present in core data
  pilot_gear: "pilot_gear",
  core_bonus: "core_bonuses",
  npc_class: "npc_classes", // ships empty in v4.0.5
  npc_template: "npc_templates", // ships empty in v4.0.5
  npc_feature: "npc_features", // ships empty in v4.0.5
  rollable_table: null, // its `tables` are name/quirk lists, not RollableTables
};

/**
 * The RAW pilot-loadout quantity limits — how many pilot weapons / armor / gear a
 * pilot may carry on foot (Core p. 34). Read off `@massif/lancer-data`'s `rules`
 * (its `lib/rules.json`), the canonical source, so the numbers can never drift from
 * a hand-typed memory of "2/1/3". Consumed by `@lancer-vtt/domain`'s pilot-gear gate
 * (`pilotGear.ts`) — the compendium package is the layer that owns the lancer-data
 * import, so the rules read lives here.
 */
export type PilotLoadoutLimits = {
  /** Max pilot weapons (`rules.max_pilot_weapons`). */
  weapon: number;
  /** Max pilot armor (`rules.max_pilot_armor`). */
  armor: number;
  /** Max pilot gear (`rules.max_pilot_gear`). */
  gear: number;
};

/** Read the pilot-loadout limits off lancer-data's `rules` block (never hardcoded). */
export function pilotLoadoutLimits(): PilotLoadoutLimits {
  const data = lancerData as { rules?: Record<string, unknown> };
  const rules = data.rules ?? {};
  return {
    weapon: Number(rules.max_pilot_weapons),
    armor: Number(rules.max_pilot_armor),
    gear: Number(rules.max_pilot_gear),
  };
}

/**
 * The RAW on-foot pilot base stats (Core p. 28) — HP/evasion/e-defense/speed/
 * sensor-range/save-target, before any bonus (grit, gear, talent, core-bonus)
 * is applied. Read off `@massif/lancer-data`'s `rules` block so the numbers can
 * never drift from a hand-typed memory. Consumed by `@lancer-vtt/rules`'
 * `computePilotStats` — `armor`/`size` have no `base_pilot_*` rules.json key
 * (COMP/CON starts both at a literal 0/0.5, `PilotStatController`), so they're not
 * read here; the compute layer supplies those two as inline constants.
 */
export type BasePilotStats = {
  /** `rules.base_pilot_hp` */
  hp: number;
  /** `rules.base_pilot_evasion` */
  evasion: number;
  /** `rules.base_pilot_edef` */
  edef: number;
  /** `rules.base_pilot_speed` */
  speed: number;
  /** `rules.base_pilot_sensors` */
  sensorRange: number;
  /** `rules.base_pilot_save_target` */
  save: number;
};

/** Read the on-foot pilot base stats off lancer-data's `rules` block (never hardcoded). */
export function basePilotStats(): BasePilotStats {
  const data = lancerData as { rules?: Record<string, unknown> };
  const rules = data.rules ?? {};
  return {
    hp: Number(rules.base_pilot_hp),
    evasion: Number(rules.base_pilot_evasion),
    edef: Number(rules.base_pilot_edef),
    speed: Number(rules.base_pilot_speed),
    sensorRange: Number(rules.base_pilot_sensors),
    save: Number(rules.base_pilot_save_target),
  };
}

/**
 * Adapt `@massif/lancer-data` (the Lancer core book, in LCP shape) into a
 * {@link RawPack} ready to feed through `parsePack`. This is the base pack every
 * Compendium starts from.
 */
export function lancerDataAsRawPack(): RawPack {
  const data = lancerData as Record<string, unknown>;
  const info = (data.info ?? {}) as { version?: string };

  return objectOfArraysToRawPack(data, DATA_KEYS, {
    name: "Lancer Core Book",
    author: "Massif Press",
    version: info.version ?? "4.0.5",
    description: "Core LANCER content via @massif/lancer-data.",
  });
}
